// WF28 resolves its Tier-4 HTTP call even when it declines to schedule — read
// status/reason, not resolution.

export type ScheduleOutcome = { kind: 'success' | 'notice' | 'error'; message: string };

// Reasons that are expected/benign, not failures — shown in a neutral tone.
const REASON_NOTICES: Record<string, string> = {
  no_active_care_period:
    'This patient has no active care period yet — content scheduling unlocks at alignment. This is expected before the patient is aligned.',
  no_active_care_period_or_catalog_item_not_found:
    'No active care period for this patient (expected before alignment), or the selected item is no longer available.',
};

function humanizeReason(reason?: string): string {
  if (!reason) return 'Could not schedule this content.';
  return reason.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function interpretScheduleResult(
  result: { status?: string; reason?: string; errors?: string[] } | null | undefined,
  label: string,
): ScheduleOutcome {
  const status = result?.status;
  if (status === 'success') return { kind: 'success', message: `Added "${label}" — drag to reorder` };
  if (status === 'already_scheduled') {
    return { kind: 'notice', message: `"${label}" is already scheduled for this patient.` };
  }
  if (status === 'error') {
    const reason = result?.reason;
    const noticeMsg = reason ? REASON_NOTICES[reason] : undefined;
    if (noticeMsg) return { kind: 'notice', message: noticeMsg };
    if (reason === 'validation_error') {
      return { kind: 'error', message: (result?.errors ?? []).join('; ') || 'Validation error' };
    }
    return { kind: 'error', message: humanizeReason(reason) };
  }
  // Unknown shape (e.g. a future decline status) — degrade to a neutral notice
  // rather than a false "Added"; the task list refetches regardless.
  return { kind: 'notice', message: 'Scheduling request submitted — the list will refresh shortly.' };
}
